import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Integration {
  integrationId: string;
  organizationId: string;
  name: string;
  integrationType: string;
  status: 'connected' | 'disconnected' | 'error';
  config: Record<string, any>;
  connectedAgents: string[];
  createdAt: string;
  updatedAt: string;
}

export const integrationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrations: builder.query<
      { integrations: Integration[]; total: number },
      { category?: string } | void
    >({
      query: (params) => {
        const category = params && 'category' in params ? params.category : undefined;
        return category ? `/integrations?category=${category}` : '/integrations';
      },
      transformResponse: (raw: unknown) =>
        normalizeKeys<{ integrations: Integration[]; total: number }>(raw),
      providesTags: ['Integration'],
    }),
    createIntegration: builder.mutation<
      Integration,
      { name: string; integrationType: string; config?: Record<string, any> }
    >({
      query: (body) => ({
        url: '/integrations',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Integration>(raw),
      invalidatesTags: ['Integration'],
    }),
    updateIntegration: builder.mutation<
      Integration,
      { id: string; body: Partial<Integration> }
    >({
      query: ({ id, body }) => ({
        url: `/integrations/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Integration>(raw),
      invalidatesTags: ['Integration'],
    }),
    deleteIntegration: builder.mutation<
      { integrationId: string; deleted: boolean },
      string
    >({
      query: (id) => ({
        url: `/integrations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Integration'],
    }),
    testIntegration: builder.mutation<
      { integrationId: string; status: string; message: string },
      string
    >({
      query: (id) => ({
        url: `/integrations/${id}/test`,
        method: 'POST',
      }),
      invalidatesTags: ['Integration'],
    }),
  }),
});

export const {
  useGetIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useTestIntegrationMutation,
} = integrationsApi;
